import React from 'react';
import { motion } from 'motion/react';
import { CheckCircle, X, Plus, Trash2 } from 'lucide-react';
import { moduleApi } from '../../lib/api';

interface CatalogItem { id: string; label: string; unit_price_ttc: number; tax_rate: number; show_tva?: boolean; }
interface ExtraLine { label: string; unit_price_ttc: number; tax_rate: number; quantity: number; }
interface Appointment {
    id: string;
    prestation_label: string;
    quantity: number;
    unit_price_ttc: number;
    amount_already_paid?: number;
}

const fmt = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);

// Équivalent, côté établissement (client/collaborateur), du "Terminer la prestation" déjà disponible côté
// salarié dans son propre portail — même route backend désormais ouverte aux deux (cf. commentaire sur
// POST /employe/prestation-planning/appointments/:id/complete), pour que l'établissement puisse aussi
// clôturer un rendez-vous (déclarer des extras, encaisser, générer la facture) sans dépendre du salarié.
export function AdminCompletionModal({ appt, catalog, onClose, onDone }: { appt: Appointment; catalog: CatalogItem[]; onClose: () => void; onDone: () => void; }) {
    const [step, setStep] = React.useState<1 | 2>(1);
    const [hasExtras, setHasExtras] = React.useState<boolean | null>(null);
    const [extras, setExtras] = React.useState<ExtraLine[]>([]);
    const tvaRates = React.useMemo(() => {
        const rates = Array.from(new Set(catalog.map((c) => c.tax_rate))).filter((r) => Number.isFinite(r));
        return rates.length > 0 ? rates : [20];
    }, [catalog]);
    const [paymentStatus, setPaymentStatus] = React.useState<'encaisse' | 'a_encaisser'>('a_encaisser');
    const [paymentEntries, setPaymentEntries] = React.useState<{ method: string; amount: number }[]>([{ method: '', amount: 0 }]);
    // Ce qui a déjà été réglé en ligne avant le rendez-vous (acompte + frais kilométriques éventuels, cf.
    // paiement avant création d'une Démarche client) — automatiquement déduit du reste à payer, jamais
    // ressaisi. Distinct du paiement éventuellement encaissé aujourd'hui (champ ci-dessous) pour que
    // l'historique des paiements (page Paiement) ne confonde jamais les deux : le montant en ligne apparaît
    // déjà séparément dans cet historique via la Démarche client d'origine, il ne doit pas être redemandé
    // ici avec un moyen de paiement "Espèces/Virement/Carte bleue" qui n'aurait aucun sens pour lui.
    const alreadyPaidOnline = Math.max(0, Number(appt.amount_already_paid) || 0);
    const [newPaymentAmount, setNewPaymentAmount] = React.useState('');
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState('');

    const addCatalogExtra = (item: CatalogItem) => {
        setExtras((prev) => [...prev, { label: item.label, unit_price_ttc: item.unit_price_ttc, tax_rate: item.show_tva !== false ? item.tax_rate : 0, quantity: 1 }]);
    };
    const addBlankExtra = () => setExtras((prev) => [...prev, { label: '', unit_price_ttc: 0, tax_rate: tvaRates[0] ?? 20, quantity: 1 }]);
    const updateExtra = (idx: number, patch: Partial<ExtraLine>) => setExtras((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
    const removeExtra = (idx: number) => setExtras((prev) => prev.filter((_, i) => i !== idx));

    const baseTotal = appt.unit_price_ttc * appt.quantity;
    const validExtras = extras.filter((e) => e.label.trim());
    const extrasTotal = validExtras.reduce((sum, e) => sum + e.unit_price_ttc * e.quantity, 0);
    const grandTotal = baseTotal + extrasTotal;
    const remainingAfterOnline = Math.max(0, grandTotal - alreadyPaidOnline);
    // Montant collecté AUJOURD'HUI (espèces/carte/virement sur place) — jamais le total déjà réglé en ligne,
    // qui reste affiché séparément et n'a besoin d'aucun moyen de paiement associé ici.
    const newMoneyToday = paymentStatus === 'encaisse' ? remainingAfterOnline : Math.min(remainingAfterOnline, Math.max(0, Number(newPaymentAmount || 0)));
    const alreadyPaidNum = Math.min(grandTotal, alreadyPaidOnline + newMoneyToday);
    const remainingDue = Math.max(0, grandTotal - alreadyPaidNum);
    const paymentMethodRequired = newMoneyToday > 0;
    const amountToAllocate = newMoneyToday;

    React.useEffect(() => {
        setPaymentEntries((prev) => prev.length <= 1 ? [{ method: prev[0]?.method || '', amount: amountToAllocate }] : prev);
    }, [amountToAllocate]);

    const updatePaymentEntry = (idx: number, patch: Partial<{ method: string; amount: number }>) =>
        setPaymentEntries((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
    const addPaymentEntry = () => {
        const allocated = paymentEntries.reduce((s, e) => s + Number(e.amount || 0), 0);
        setPaymentEntries((prev) => [...prev, { method: '', amount: Math.max(0, Math.round((amountToAllocate - allocated) * 100) / 100) }]);
    };
    const removePaymentEntry = (idx: number) => setPaymentEntries((prev) => prev.filter((_, i) => i !== idx));
    const paymentEntriesSum = paymentEntries.reduce((s, e) => s + Number(e.amount || 0), 0);
    const paymentEntriesValid = !paymentMethodRequired || (
        paymentEntries.every((e) => e.method && Number(e.amount) > 0) &&
        Math.abs(paymentEntriesSum - amountToAllocate) < 0.01
    );

    const handleValidate = async () => {
        setSubmitting(true);
        setError('');
        try {
            const res: any = await moduleApi.completePrestationAppointment(appt.id, {
                extras: hasExtras ? validExtras : [],
                payment_status: paymentStatus,
                payment_entries: paymentMethodRequired ? paymentEntries : [],
                new_payment_amount: newMoneyToday,
            });
            if (res?.error) { setError(res.error); setSubmitting(false); return; }

            // L'email de fin de prestation part quoi qu'il arrive — la facture PDF n'est jointe que si le
            // module Facture est actif pour cet établissement (auquel cas res.invoice est renseigné).
            if (res?.recipient_email) {
                try {
                    let pdfBase64: string | undefined, filename: string | undefined;
                    if (res.invoice) {
                        const { generateInvoicePdfFromData } = await import('../Factures/invoicePdfGenerator');
                        const { pdf } = await generateInvoicePdfFromData({ invoice: res.invoice, payload: res.payload, snapshot: res.snapshot });
                        const dataUri: string = pdf.output('datauristring');
                        pdfBase64 = dataUri.split(',')[1];
                        filename = `${res.invoice?.invoice_number || 'facture'}.pdf`;
                    }
                    await moduleApi.sendPrestationInvoiceEmail(appt.id, { to: res.recipient_email, pdfBase64, filename });
                } catch (pdfErr) {
                    console.error('Failed to generate/send invoice email', pdfErr);
                }
            }

            onDone();
        } catch (e: any) {
            setError('Erreur lors de la clôture');
            setSubmitting(false);
        }
    };

    const pillClass = (active: boolean) => `flex-1 px-4 py-2.5 rounded-xl text-sm font-bold border transition-all ${active ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`;

    return (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-md bg-surface border border-border rounded-3xl max-h-[85vh] shadow-2xl flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
                    <h3 className="font-bold text-content flex items-center gap-2"><CheckCircle size={18} className="text-emerald-400" /> Terminer la prestation</h3>
                    <button onClick={onClose} className="p-1.5 text-content-muted hover:text-content rounded-lg hover:bg-surface-soft"><X size={18} /></button>
                </div>
                <div className="p-5 space-y-4 overflow-y-auto">
                    {step === 1 ? (
                        <>
                            <p className="text-sm text-content font-semibold">Y a-t-il eu des extras pendant le rendez-vous ?</p>
                            <div className="flex gap-2">
                                <button onClick={() => setHasExtras(false)} className={pillClass(hasExtras === false)}>Non</button>
                                <button onClick={() => setHasExtras(true)} className={pillClass(hasExtras === true)}>Oui</button>
                            </div>
                            {hasExtras && (
                                <div className="space-y-3">
                                    {catalog.length > 0 && (
                                        <select
                                            onChange={(e) => { const item = catalog.find((c) => c.id === e.target.value); if (item) addCatalogExtra(item); e.target.value = ''; }}
                                            defaultValue=""
                                            className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500"
                                        >
                                            <option value="">+ Ajouter depuis le catalogue...</option>
                                            {catalog.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                                        </select>
                                    )}
                                    <button type="button" onClick={addBlankExtra} className="text-xs font-bold text-brand-600 dark:text-brand-400 flex items-center gap-1.5">
                                        <Plus size={14} /> Saisie libre d'une ligne
                                    </button>
                                    {extras.map((ex, idx) => (
                                        <div key={idx} className="flex items-center gap-2 bg-surface-soft border border-border rounded-xl p-2">
                                            <input value={ex.label} onChange={(e) => updateExtra(idx, { label: e.target.value })} placeholder="Description"
                                                className="flex-1 min-w-0 bg-transparent text-sm text-content outline-none" />
                                            <input type="number" min={1} step="1" value={ex.quantity}
                                                onChange={(e) => updateExtra(idx, { quantity: Math.max(1, Number(e.target.value || 1)) })}
                                                placeholder="Qté" title="Quantité"
                                                className="w-12 bg-transparent text-sm text-content outline-none text-right" />
                                            <input type="number" min={0} step="0.01" value={ex.unit_price_ttc}
                                                onChange={(e) => updateExtra(idx, { unit_price_ttc: Number(e.target.value || 0) })}
                                                placeholder="TTC" title="Montant TTC unitaire"
                                                className="w-16 bg-transparent text-sm text-content outline-none text-right" />
                                            <select value={ex.tax_rate} onChange={(e) => updateExtra(idx, { tax_rate: Number(e.target.value) })}
                                                title="TVA" className="shrink-0 bg-transparent text-xs text-content-muted outline-none">
                                                {(tvaRates.includes(ex.tax_rate) ? tvaRates : [...tvaRates, ex.tax_rate]).map((r) => (
                                                    <option key={r} value={r}>{r}%</option>
                                                ))}
                                            </select>
                                            <button type="button" onClick={() => removeExtra(idx)} className="text-red-400 p-1 shrink-0"><Trash2 size={14} /></button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <p className="text-sm text-content font-semibold">La prestation a-t-elle été payée ?</p>
                            <div className="flex gap-2">
                                <button onClick={() => setPaymentStatus('encaisse')} className={pillClass(paymentStatus === 'encaisse')}>ENCAISSER</button>
                                <button onClick={() => setPaymentStatus('a_encaisser')} className={pillClass(paymentStatus === 'a_encaisser')}>À ENCAISSER</button>
                            </div>
                            {alreadyPaidOnline > 0 && (
                                <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-between text-sm">
                                    <span className="text-emerald-400 font-semibold">Déjà réglé en ligne</span>
                                    <span className="font-bold text-emerald-400">{fmt(alreadyPaidOnline)}</span>
                                </div>
                            )}
                            {paymentStatus === 'a_encaisser' && remainingAfterOnline > 0 && (
                                <div className="space-y-1">
                                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Montant supplémentaire encaissé aujourd'hui (optionnel)</label>
                                    <input type="number" min={0} step="0.01" value={newPaymentAmount}
                                        onChange={(e) => setNewPaymentAmount(e.target.value)}
                                        placeholder="Ex: 50"
                                        className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                </div>
                            )}
                            {paymentMethodRequired && (
                                <div className="space-y-2">
                                    <p className="text-xs font-bold text-content-muted uppercase tracking-widest">Moyen de paiement (pour le montant encaissé aujourd'hui)</p>
                                    {paymentEntries.map((entry, idx) => (
                                        <div key={idx} className="space-y-1.5 p-2 rounded-xl bg-surface-soft border border-border">
                                            <div className="flex gap-2">
                                                {['Espèces', 'Virement', 'Carte bleue'].map((m) => (
                                                    <button key={m} onClick={() => updatePaymentEntry(idx, { method: m })}
                                                        className={`flex-1 px-3 py-2 rounded-xl text-xs font-bold border transition-all ${entry.method === m ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                                        {m}
                                                    </button>
                                                ))}
                                            </div>
                                            {paymentEntries.length > 1 && (
                                                <div className="flex items-center gap-2">
                                                    <input type="number" min={0} step="0.01" value={entry.amount}
                                                        onChange={(e) => updatePaymentEntry(idx, { amount: Number(e.target.value || 0) })}
                                                        placeholder="Montant" title="Montant pour ce moyen de paiement"
                                                        className="flex-1 bg-surface border border-border rounded-lg px-2 py-1.5 text-sm text-content outline-none focus:border-brand-500" />
                                                    <button type="button" onClick={() => removePaymentEntry(idx)} className="text-red-400 p-1 shrink-0"><Trash2 size={14} /></button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    <button type="button" onClick={addPaymentEntry} className="text-xs font-bold text-brand-600 dark:text-brand-400 flex items-center gap-1.5">
                                        <Plus size={14} /> Ajouter un autre moyen de paiement
                                    </button>
                                    {paymentEntries.length > 1 && Math.abs(paymentEntriesSum - amountToAllocate) >= 0.01 && (
                                        <p className="text-xs text-red-400 font-semibold">
                                            La somme des moyens de paiement ({fmt(paymentEntriesSum)}) ne correspond pas au montant à régler ({fmt(amountToAllocate)}).
                                        </p>
                                    )}
                                </div>
                            )}
                            <div className="p-4 rounded-xl bg-surface-soft border border-border space-y-1.5 text-sm">
                                <div className="flex justify-between text-content-muted">
                                    <span>{appt.prestation_label}{appt.quantity > 1 ? ` × ${appt.quantity}` : ''}</span>
                                    <span>{fmt(baseTotal)}</span>
                                </div>
                                {validExtras.map((e, i) => (
                                    <div key={i} className="flex justify-between text-content-muted">
                                        <span>{e.label}{e.quantity > 1 ? ` × ${e.quantity}` : ''}</span>
                                        <span>{fmt(e.unit_price_ttc * e.quantity)}</span>
                                    </div>
                                ))}
                                {paymentStatus === 'a_encaisser' && alreadyPaidNum > 0 && (
                                    <div className="flex justify-between text-content-muted">
                                        <span>Déjà réglé</span>
                                        <span>- {fmt(alreadyPaidNum)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between pt-2 border-t border-border font-bold text-content">
                                    <span>{paymentStatus === 'encaisse' ? 'TOTAL ENCAISSÉ' : 'TOTAL NET À PAYER'}</span>
                                    <span className={paymentStatus === 'encaisse' ? 'text-emerald-400' : 'text-red-400'}>{fmt(paymentStatus === 'encaisse' ? grandTotal : remainingDue)}</span>
                                </div>
                            </div>
                        </>
                    )}
                    {error && <p className="text-xs text-red-400 font-semibold">{error}</p>}
                </div>
                <div className="flex justify-between items-center p-5 border-t border-border shrink-0">
                    <button onClick={step === 1 ? onClose : () => setStep(1)} className="px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content text-sm font-bold">
                        {step === 1 ? 'Annuler' : 'Retour'}
                    </button>
                    {step === 1 ? (
                        <button onClick={() => setStep(2)} disabled={hasExtras === null} className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">Suivant</button>
                    ) : (
                        <button onClick={handleValidate} disabled={submitting || !paymentEntriesValid} className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">
                            {submitting ? 'Validation...' : 'Valider'}
                        </button>
                    )}
                </div>
            </motion.div>
        </div>
    );
}
